import React from 'react';
import { FeatureRow, SectionLabel } from './FeatureRow';
import { useWelcomeOffer } from '../../context/WelcomeOfferContext';
import { applyDiscount, applyCarePlanMode } from './priceMath';
import { CARE_PRICES } from '../../constants/pricing';
import { MONO } from '../../constants/fonts';

const BASE_BUILD_PRICE = 300;
const WORKED_EXAMPLE_PRICE = 500;
const ESSENTIAL_CARE_PRICE = CARE_PRICES.website.essential;
const ACTIVE_CARE_PRICE = CARE_PRICES.website.active;

function renderCarePrice(original, mode, percent, custom) {
  if (mode === 'none') return `£${original}`;
  return (
    <>
      <span style={s.strikePrice}>£{original}</span>
      {mode === 'free' ? 'Free*' : `£${applyCarePlanMode(original, mode, percent, custom)}`}
    </>
  );
}

function WebsitePackages() {
  const { offer } = useWelcomeOffer();
  const discountPercent = offer?.offerType === 'discount' ? offer.discountPercent : null;
  const isFreeBuild = offer?.offerType === 'free';
  const isWebsiteCategory = (offer?.category || 'website') !== 'app';
  const essentialMode = isWebsiteCategory ? (offer?.essentialPlanMode || 'none') : 'none';
  const essentialPercent = offer?.essentialPlanPercent;
  const essentialCustom = offer?.essentialPlanCustom;
  const activeMode = isWebsiteCategory ? (offer?.activePlanMode || 'none') : 'none';
  const activePercent = offer?.activePlanPercent;
  const activeCustom = offer?.activePlanCustom;

  return (
    <>
      <style>{`
        .pricing-card { transition: transform 0.18s ease, border-color 0.18s ease; }
        .pricing-card:hover { transform: translateY(-4px); }
      `}</style>

      <div>
        <SectionLabel>Website packages</SectionLabel>
        <h2 style={s.sectionHeadline}>A site that works as hard as you do.</h2>
        <p style={{ ...s.sectionSub, marginBottom: 40 }}>
          Built on a simple, transparent build cost. Pay for exactly what you need.
          Prices below are a guide. Your quote may vary depending on scope and complexity.
        </p>

        {/* Build pricing grid */}
        <div className="build-row" style={s.buildGrid}>

          <div className="pricing-card" style={{ ...s.buildCard, borderColor: 'rgba(99,102,241,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <p style={s.cardLabel}>Base build</p>
                <p style={{ ...s.cardPrice, color: '#818cf8' }}>
                  {isFreeBuild ? (
                    <>
                      <span style={s.strikePrice}>£{BASE_BUILD_PRICE}</span>
                      Free*
                    </>
                  ) : discountPercent ? (
                    <>
                      <span style={s.strikePrice}>£{BASE_BUILD_PRICE}</span>
                      £{applyDiscount(BASE_BUILD_PRICE, discountPercent)}
                    </>
                  ) : `£${BASE_BUILD_PRICE}`}
                </p>
              </div>
              <span style={{ ...s.badge, background: 'rgba(99,102,241,0.12)', color: '#818cf8' }}>
                Guide price
              </span>
            </div>
            <p style={s.cardDesc}>
              One page (home), domain setup, CMS, hosting configuration. Everything you need to exist online.
            </p>
            <div style={s.featureList}>
              <FeatureRow label="Home page, possibly another simple screen" included />
              <FeatureRow label="Domain acquisition & configuration (your-website-name.com)" included />
              <FeatureRow label="CMS so you can edit content yourself" included />
              <FeatureRow label="Hosting setup & go-live" included />
            </div>
          </div>

          <div style={s.addOnsCard}>
            <p style={s.cardLabel}>Add-on pages</p>
            <div style={s.addOnsList}>
              <div style={s.addOnRow}>
                <div>
                  <span style={s.addOnName}>Simple page</span>
                  <span style={s.addOnDesc}>About, Contact, basic text</span>
                </div>
                <span style={{ ...s.addOnPrice, color: '#34d399' }}>+£100</span>
              </div>
              <div style={s.addOnDivider} />
              <div style={s.addOnRow}>
                <div>
                  <span style={s.addOnName}>Complex page</span>
                  <span style={s.addOnDesc}>Services, galleries, structured sections</span>
                </div>
                <span style={{ ...s.addOnPrice, color: '#34d399' }}>+£150</span>
              </div>
              <div style={s.addOnDivider} />
              <div style={s.addOnRow}>
                <div>
                  <span style={s.addOnName}>Unique feature</span>
                  <span style={s.addOnDesc}>Booking form, map, custom widget</span>
                </div>
                <span style={{ ...s.addOnPrice, color: '#34d399' }}>+£50</span>
              </div>
              <div style={s.addOnDivider} />
              <div style={s.addOnRow}>
                <div>
                  <span style={s.addOnName}>Tailored CMS</span>
                  <span style={s.addOnDesc}>Custom admin interface</span>
                </div>
                <span style={{ ...s.addOnPrice, color: '#9ca3af' }}>POA</span>
              </div>
            </div>
            <div style={s.workedExample}>
              <span style={s.workedExampleLabel}>Example</span>
              <span style={s.workedExampleText}>
                Home + About + Contact = {isFreeBuild ? (
                  <>
                    <span style={s.strikePriceInline}>£{WORKED_EXAMPLE_PRICE}</span>{' '}
                    <strong style={{ color: '#e0e0f0' }}>Free*</strong>
                  </>
                ) : discountPercent ? (
                  <>
                    <span style={s.strikePriceInline}>£{WORKED_EXAMPLE_PRICE}</span>{' '}
                    <strong style={{ color: '#e0e0f0' }}>£{applyDiscount(WORKED_EXAMPLE_PRICE, discountPercent)}</strong>
                  </>
                ) : (
                  <strong style={{ color: '#e0e0f0' }}>£{WORKED_EXAMPLE_PRICE}</strong>
                )}
              </span>
            </div>
          </div>

        </div>

        {/* Friendly note */}
        <div style={s.monstersNote}>
          <span style={s.monstersQuote}>"</span>
          We like to leave a little room for the good stuff. The odd extra feature may find its way in.
          <span style={s.monstersQuote}>"</span>
        </div>

        {/* Web care plans */}
        <p id="web-care-plans" style={{ ...s.cardLabel, margin: '40px 0 20px', color: '#9ca3af' }}>Web care plans</p>
        <div className="care-grid" style={s.careGrid}>

          <div className="pricing-card" style={s.careCard}>
            <div style={{ marginBottom: 16 }}>
              <p style={s.carePlanName}>Essential</p>
              <p style={{ ...s.carePrice, color: '#d8dae8' }}>
                {renderCarePrice(ESSENTIAL_CARE_PRICE, essentialMode, essentialPercent, essentialCustom)}
                {essentialMode !== 'free' && <span style={s.perMonth}>/mo</span>}
              </p>
            </div>
            <div style={s.featureList}>
              <FeatureRow label="Hosting included" included />
              <FeatureRow label="Immediate issue response" included />
              <FeatureRow label="Security updates & maintenance" included />
              <FeatureRow label="Domain management" included />
              <FeatureRow label="Analytics & monthly report" />
            </div>
          </div>

          <div className="pricing-card" style={{ ...s.careCard, borderColor: 'rgba(129,140,248,0.35)', background: 'rgba(99,102,241,0.06)' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={s.carePlanName}>Active</p>
                <span style={{ ...s.badge, background: 'rgba(129,140,248,0.15)', color: '#818cf8', fontSize: '0.62rem' }}>
                  Recommended
                </span>
              </div>
              <p style={{ ...s.carePrice, color: '#818cf8' }}>
                {renderCarePrice(ACTIVE_CARE_PRICE, activeMode, activePercent, activeCustom)}
                {activeMode !== 'free' && <span style={s.perMonth}>/mo</span>}
              </p>
            </div>
            <div style={s.featureList}>
              <FeatureRow label="Everything in Essential" included />
              <FeatureRow label="Analytics & monthly report" included />
              <FeatureRow label="Quarterly backup" included />
              <FeatureRow label="Priority response" included />
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

const s = {
  sectionHeadline: {
    fontSize: 'clamp(1.6rem, 5vw, 2.6rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.15,
    color: '#f0f0f5',
    margin: '0 0 16px',
  },
  sectionSub: {
    fontSize: '0.92rem',
    color: '#6b7080',
    lineHeight: 1.75,
    maxWidth: 560,
    margin: '0 0 40px',
  },
  buildGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  buildCard: {
    background: '#16161c',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: '28px 24px',
  },
  addOnsCard: {
    background: '#13131a',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 20,
    padding: '28px 24px',
  },
  cardLabel: {
    fontFamily: MONO,
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#4a4f66',
    margin: '0 0 8px',
  },
  cardPrice: {
    fontSize: 'clamp(1.8rem, 4vw, 2.4rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: '#818cf8',
    margin: 0,
    lineHeight: 1,
  },
  strikePrice: {
    fontSize: '0.5em',
    fontWeight: 600,
    color: '#4a4f66',
    textDecoration: 'line-through',
    marginRight: 6,
  },
  strikePriceInline: {
    fontWeight: 600,
    color: '#6b7080',
    textDecoration: 'line-through',
  },
  cardDesc: {
    fontSize: '0.84rem',
    color: '#6b7080',
    lineHeight: 1.7,
    margin: '0 0 20px',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  badge: {
    fontFamily: MONO,
    fontSize: '0.64rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '4px 10px',
    borderRadius: 20,
    flexShrink: 0,
  },
  addOnsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  addOnRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    gap: 12,
  },
  addOnName: {
    display: 'block',
    fontSize: '0.86rem',
    fontWeight: 600,
    color: '#b0b4cc',
    lineHeight: 1.3,
  },
  addOnDesc: {
    display: 'block',
    fontSize: '0.72rem',
    color: '#4a4f66',
    marginTop: 2,
  },
  addOnPrice: {
    fontFamily: MONO,
    fontSize: '1rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  addOnDivider: {
    height: 1,
    background: 'rgba(255,255,255,0.05)',
  },
  workedExample: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 20,
    padding: '14px 16px',
    background: 'rgba(52,211,153,0.06)',
    borderRadius: 12,
    border: '1px solid rgba(52,211,153,0.12)',
  },
  workedExampleLabel: {
    fontFamily: MONO,
    fontSize: '0.65rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#34d399',
  },
  workedExampleText: {
    fontSize: '0.82rem',
    color: '#6b7080',
  },
  monstersNote: {
    margin: '28px 0 0',
    padding: '16px 22px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px dashed rgba(255,255,255,0.08)',
    borderRadius: 12,
    fontSize: '0.88rem',
    color: '#6b7080',
    fontStyle: 'italic',
    lineHeight: 1.6,
    textAlign: 'center',
  },
  monstersQuote: {
    fontFamily: MONO,
    fontSize: '1.1rem',
    color: 'rgba(255,255,255,0.1)',
    fontStyle: 'normal',
    margin: '0 4px',
  },
  careGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  careCard: {
    background: '#16161c',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: '24px',
  },
  carePlanName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#d8dae8',
    margin: '0 0 4px',
  },
  carePrice: {
    fontSize: '2rem',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    margin: 0,
    lineHeight: 1,
  },
  perMonth: {
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#4a4f66',
    marginLeft: 2,
  },
};

export default WebsitePackages;
